import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as turf from '@turf/turf';
import getUSAStateForPoint from '../utils/get-usa-state-for-point';
import { acresPerSqMeter } from '../constants/units';
import {
  useCalculateSuitability,
  useTilesets,
} from '../components/portfolio-evaluator/helpers';
import useAddClippableTilesetLayers from '../hooks/useAddClippableTilesetLayers';
import { layerIdForTileset } from '../utils/tilesets';

const initialState = {
  mapRef: {},
  mapLoaded: false,
  setMapLoaded: () => {},
  map: null,
  features: [],
  setFeatures: () => {},
  clearFeatures: () => {},
  hoveredFeatureId: null,
  setHoveredFeatureId: () => {},
  selectedFeatureId: null,
  setSelectedFeatureId: () => {},
  selectedTilesetId: null,
  setSelectedTilesetId: () => {},
  tilesets: [],
  tilesetLayers: [],
  cropOptions: [],
  selectedCrops: [],
  setSelectedCrops: () => {},
  loadSuitability: () => {},
  loadingState: null,
  suitabilityData: null,
};

export const PortfolioEvaluatorContext = createContext(initialState);

export const PortfolioEvaluatorContextProvider = ({ children }) => {
  const mapRef = useRef();

  const [mapLoaded, setMapLoaded] = useState(false);
  const [features, setFeatures] = useState([]);
  const [hoveredFeatureId, setHoveredFeatureId] = useState(null);
  const [selectedFeatureId, setSelectedFeatureId] = useState(null);
  const [selectedTilesetId, setSelectedTilesetId] = useState(null);

  const setAndAugmentFeatures = async (newFeatures) => {
    const augmentedFeatures = await Promise.all(
      newFeatures.map(async (feature) => {
        const { geometry } = feature;
        if (geometry?.type !== 'Polygon' && geometry?.type !== 'MultiPolygon') {
          console.error(`Unexpected geometry type ${geometry?.type}`, feature);
          return {
            ...feature,
            properties: { ...feature.properties, area: 0, usState: null },
          };
        }
        // Get the state of a plot based on its center
        const center = turf.centerOfMass(feature).geometry.coordinates;
        const lat = center[1];
        const lng = center[0];
        const usState = await getUSAStateForPoint({ lat, lng });

        // Get the plot's area
        // By default, returns area in square meters, so convert to acres
        const areaInAcres = turf.area(feature) * acresPerSqMeter;

        return {
          ...feature,
          properties: {
            ...feature.properties,
            area: areaInAcres,
            usState,
          },
        };
      }),
    );
    setFeatures(augmentedFeatures);
  };

  const map = mapLoaded ? mapRef.current : null;
  const {
    tilesets,
    cropOptions,
    selectedCrops,
    setSelectedCrops,
  } = useTilesets();

  const [suitabilityData, setSuitabilityData] = useState(null);
  const { loadingState, calculateSuitability } = useCalculateSuitability(
    map,
    features,
    tilesets,
  );

  // Add the suitability raster tileset layers to the map
  const tilesetLayers = useAddClippableTilesetLayers(map, tilesets);

  // Update the clipping masks for the suitability layers
  useEffect(() => {
    tilesetLayers.forEach((layer) => {
      layer.updateMask(features);
    });
  }, [tilesetLayers, features]);

  useEffect(() => {
    if (!map) return;

    const layerId = selectedTilesetId && layerIdForTileset(selectedTilesetId);
    const layer =
      selectedTilesetId && tilesetLayers.find((l) => l.id === layerId);

    // show the selected raster layer (if there is one)
    if (layer) layer.updateOpacity(1);

    // hide all other raster layers
    tilesetLayers.forEach((l) => {
      if (l.id !== layerId) l.updateOpacity(0);
    });

    // trigger repaint to update the map
    map.triggerRepaint();
  }, [map, tilesetLayers, selectedTilesetId]);

  const loadSuitability = useCallback(async () => {
    const data = await calculateSuitability();
    setSuitabilityData(data);
  }, [calculateSuitability]);

  // Reset suitability data when features change to avoid displaying incorrect totals
  const setFeaturesAndResetSuitability = useCallback(async (f) => {
    await setAndAugmentFeatures(f);
    setSuitabilityData(null);
  }, []);

  const value = useMemo(
    () => ({
      mapRef,
      mapLoaded,
      setMapLoaded,
      map,
      features,
      setFeatures: setFeaturesAndResetSuitability,
      clearFeatures: () => setFeatures([]),
      hoveredFeatureId,
      setHoveredFeatureId,
      selectedFeatureId,
      setSelectedFeatureId,
      selectedTilesetId,
      setSelectedTilesetId,
      tilesets,
      tilesetLayers,
      cropOptions,
      selectedCrops,
      setSelectedCrops,
      loadSuitability,
      loadingState,
      suitabilityData,
    }),
    [
      cropOptions,
      features,
      hoveredFeatureId,
      loadSuitability,
      loadingState,
      map,
      mapLoaded,
      selectedCrops,
      selectedFeatureId,
      selectedTilesetId,
      setFeaturesAndResetSuitability,
      setSelectedCrops,
      suitabilityData,
      tilesetLayers,
      tilesets,
    ],
  );

  return (
    <PortfolioEvaluatorContext.Provider value={value}>
      {children}
    </PortfolioEvaluatorContext.Provider>
  );
};

export const usePortfolioEvaluatorContext = () =>
  useContext(PortfolioEvaluatorContext);
